import {
    type DataSourceAbstract,
    type EVENT_VARIABLE_TYPE,
    IndependentAction,
    LOOP_VARIABLE_VALUE,
    type TypeInstanceMap
} from '@lighthouse/core'
import type { FlowNode, VariableTree2 } from '@lighthouse/shared'
import {
    getDefaultDataSourceOptions,
    getEventOption,
    getFilterControllerVariableOption,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getInputVariableOption,
    getLoopVariableOption,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    getVirtualFieldByInnerType,
    NodeFieldPreview,
    placeholderFromInnerType,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import type { JSONContent } from '@tiptap/core'
import { find } from 'rambda'
import React, { useCallback, useDeferredValue, useMemo } from 'react'

import { availableFieldBlocksAtom, availableFilterBlocksAtom, blockHighlightAtom, lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

export type LoopInfo = {
    open: boolean
    name: string
}

interface SettingSubProcessParamProps {
    value?: JSONContent
    dataSource?: DataSourceAbstract
    innerType: TypeInstanceMap
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    loop?: LoopInfo
    parentNodes?: FlowNode[]
    dataSourceList: DataSourceAbstract[]
    eventType?: EVENT_VARIABLE_TYPE
    onChange: (val: JSONContent) => void
}

export const SettingSubProcessParam: React.FC<SettingSubProcessParamProps> = ({
    value,
    dataSource,
    innerType: paramInnerType,
    allParentNodes,
    allParentActions,
    loop,
    parentNodes,
    dataSourceList,
    eventType,
    onChange
}) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const { type } = useFlowContainerContext()
    const { run: setBlockHighlight } = useAtomAction(blockHighlightAtom)
    const handleMouseEnter = useCallback(
        (id: string) => {
            setBlockHighlight([id])
            document.querySelector(`div[data-node-id="${id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        },
        [setBlockHighlight]
    )

    const handleMouseLeave = useCallback(() => {
        setBlockHighlight([])
    }, [setBlockHighlight])

    const availableFieldBlocksInfo = useDeferredValue(useAtomData(availableFieldBlocksAtom))
    const availableFilterBlocksInfo = useDeferredValue(useAtomData(availableFilterBlocksAtom))
    const { viewType, viewDsId, formDsId, isImage } = useActionAdderDepParams()
    const isInFlow = !!type
    const virtualField = useMemo(() => getVirtualFieldByInnerType(paramInnerType), [paramInnerType])
    const workflowList = useAtomData(workflowListAtom)
    const { customViewOption } = useVariableCustomViewOption()

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    const formOption = useMemo(() => {
        if (formDsId && !isInFlow) {
            return getFormDatasourceOptions({
                dataSource: find(ds => ds.id === formDsId, dataSourceList)
            })
        }
    }, [dataSourceList, formDsId, isInFlow])

    const loopOption = useMemo(() => {
        if (loop && loop.open) {
            return getLoopVariableOption({
                name: loop.name,
                data: [LOOP_VARIABLE_VALUE.LOOP_TIMES],
                filter: undefined
            })
        }
    }, [loop])

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDsId) {
            const viewOption = getViewVariableOption({
                dataSource: find(ds => ds.id === viewDsId, dataSourceList),
                viewType
            })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, dataSourceList, viewType, viewDsId])

    const datasourceOption = useMemo(() => {
        if (!dataSource) {
            return
        }
        return getDefaultDataSourceOptions(dataSource)
    }, [dataSource])

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const inputOption = useMemo(() => {
        if (isInFlow) {
            return
        }
        if (availableFieldBlocksInfo.length === 0) {
            return
        }
        return getInputVariableOption(availableFieldBlocksInfo, dataSourceList, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [dataSourceList, availableFieldBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const filterOption = useMemo(() => {
        if (availableFilterBlocksInfo.length === 0 || isInFlow) {
            return
        }
        return getFilterControllerVariableOption(availableFilterBlocksInfo, {
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave
        })
    }, [availableFilterBlocksInfo, handleMouseEnter, handleMouseLeave, isInFlow])

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: parentNodes,
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, parentNodes, workflowList])
    const eventOption = useMemo(() => eventType && getEventOption(eventType, paramInnerType), [eventType, paramInnerType])

    const placeholder = placeholderFromInnerType[virtualField?.innerType || 'TEXT']

    return (
        <NodeFieldPreview
            innerTypes={getInputInnerTypeMatch(virtualField?.innerType)}
            disabled={disabledWithVersion}
            autoHeight
            // onChange={onChange}
            onBlur={onChange}
            value={value}
            placeholder={placeholder}
            field={virtualField}
            pageOptions={pageOptions}
            upstreamOptions={upstreamOptions}
            inputOption={inputOption}
            filterOption={filterOption}
            eventOption={eventOption}
            loopOption={loopOption}
            datasourceOption={datasourceOption}
            userOption={userOption}
            viewOptions={viewOptions}
            formOption={formOption}
            imageOption={imageOption}
            dataSourceList={dataSourceList}
        />
    )
}
